// Funciones auxiliares para PropertyView
// Cálculos de tiempo, formatos, estados y otras utilidades

import { LAST_CHANCE_DURATION } from '../config/lastChanceConfig';

/**
 * Genera fecha por defecto para inicio de pujas (mañana a las 00:00)
 * @returns {string} Fecha en formato datetime-local (YYYY-MM-DDTHH:mm)
 */
export const getDefaultStartDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow.toISOString().slice(0, 16);
};

/**
 * Genera fecha por defecto para cierre de pujas (+7 días a las 23:59)
 * @returns {string} Fecha en formato datetime-local (YYYY-MM-DDTHH:mm)
 */
export const getDefaultEndDate = () => {
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 7);
  endDate.setHours(23, 59, 0, 0);
  return endDate.toISOString().slice(0, 16);
};

/**
 * Calcula el tiempo restante hasta una fecha límite con formato adaptativo
 * Formato cambia según tiempo restante para mejor UX:
 * - Más de 24h: "2d 15h" (días y horas)
 * - Entre 1h y 24h: "15h 42m" (horas y minutos)
 * - Menos de 1h: "42m 18s" (minutos y segundos)
 * @param {string} deadline - Fecha límite en formato ISO string
 * @returns {string} Tiempo restante formateado o "Finalizado" si expiró
 * @example
 * calculateTimeLeft('2026-02-10T23:59:00') // "5d 18h" (si faltan 5 días)
 * calculateTimeLeft('2026-02-05T15:30:00') // "12h 45m" (si faltan 12 horas)
 * calculateTimeLeft('2026-02-05T14:15:00') // "45m 20s" (si faltan 45 minutos)
 */
export const calculateTimeLeft = (deadline, t) => {
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const diff = deadlineDate - now;

  if (diff < 0) return t ? t('time.finished') : 'Finalizado';

  // Calcular componentes de tiempo
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  // Estado 1: Más de 24 horas → "2d 15h"
  if (diff > 24 * 60 * 60 * 1000) {
    return `${days}d ${hours}h`;
  }

  // Estado 2: Entre 1 hora y 24 horas → "15h 42m"
  if (diff > 60 * 60 * 1000) {
    return `${hours}h ${minutes}m`;
  }

  // Estado 3: Menos de 1 hora → "42m 18s"
  return `${minutes}m ${seconds}s`;
};

/**
 * Calcula el tiempo transcurrido desde una fecha (formato relativo)
 * @param {string} timestamp - Fecha en formato ISO string
 * @returns {string} Tiempo transcurrido formateado (ej: "hace 5min", "hace 2h", "hace 3d")
 */
export const getTimeSince = (timestamp, t) => {
  const now = new Date();
  const bidTime = new Date(timestamp);
  const diff = now - bidTime;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (t) {
    if (days > 0) return t('time.daysAgo', { count: days });
    if (hours > 0) return t('time.hoursAgo', { count: hours });
    if (minutes > 0) return t('time.minutesAgo', { count: minutes });
    return t('time.justNow');
  }

  if (days > 0) return `hace ${days}d`;
  if (hours > 0) return `hace ${hours}h`;
  if (minutes > 0) return `hace ${minutes}min`;
  return 'hace un momento';
};

/**
 * Calcula el estado actual de una propiedad basado en fechas y flags
 * Evalúa todas las fases de subasta en orden: BLIND → OPEN → LAST_CHANCE → CLOSED
 * @param {Object} property - Objeto de propiedad completo
 * @returns {string} Estado actual: 'UPCOMING' | 'BLIND_WAITING' | 'BLIND_ACTIVE' | 'OPEN' | 'LAST_CHANCE' | 'CLOSED'
 * @note Respeta cierres manuales (blind_bidding_closed_at, last_chance_closed_at)
 */
export const getPropertyStatus = (property) => {
  if (!property) return 'CLOSED';

  const now = new Date();
  const startDate = property.biddingStartDate ? new Date(property.biddingStartDate) : null;
  const deadline = property.deadline ? new Date(property.deadline) : null;

  // Calcular fin del periodo de última oportunidad
  // Prioridad: usar deadline si existe, si no calcular con duration (legacy)
  const lastChanceEnd = property.last_chance_deadline
    ? new Date(property.last_chance_deadline)
    : property.last_chance_started_at
    ? (() => {
        const startDate = new Date(property.last_chance_started_at);
        const duration = property.last_chance_duration || LAST_CHANCE_DURATION;
        return new Date(startDate.getTime() + duration * 60 * 60 * 1000);
      })()
    : null;

  // Calcular fin del periodo de oferta ciega
  // Prioridad: usar deadline si existe, si no calcular con duration (legacy)
  const blindBiddingEnd = property.blind_bidding_deadline
    ? new Date(property.blind_bidding_deadline)
    : property.blind_bidding_started_at
    ? (() => {
        const blindStart = new Date(property.blind_bidding_started_at);
        const duration = property.blind_bidding_duration || 72;
        return new Date(blindStart.getTime() + duration * 60 * 60 * 1000);
      })()
    : null;

  // ==========================================
  // PRIORIDAD 1: LAST CHANCE (fase final)
  // ==========================================

  // Cerrada manualmente → CLOSED
  if (property.last_chance_closed_at) {
    return 'CLOSED';
  }

  // Activa → LAST_CHANCE
  if (property.last_chance_started_at && lastChanceEnd && now < lastChanceEnd) {
    return 'LAST_CHANCE';
  }

  // Terminada → CLOSED
  if (lastChanceEnd && now >= lastChanceEnd) {
    return 'CLOSED';
  }

  // ==========================================
  // PRIORIDAD 2: FASES DE OFERTA CIEGA
  // ==========================================

  // CLOSED: Cerrada manualmente con blind_bidding_closed_at (verificar PRIMERO)
  if (property.blind_bidding_closed_at) {
    // Si hay biddingStartDate posterior al cierre → verificar si está en OPEN
    const blindClosedAt = new Date(property.blind_bidding_closed_at);
    if (startDate && startDate > blindClosedAt) {
      // Se configuró subasta abierta después de cerrar blind
      if (now >= startDate && now < deadline) {
        return 'OPEN';
      }
      // Deadline pasó → CLOSED (esperando Last Chance manual)
      return 'CLOSED';
    }
    // No se ha configurado subasta abierta → CLOSED (esperando config)
    return 'CLOSED';
  }

  // BLIND_WAITING: Esperando primera oferta
  if (property.blind_bidding_deadline && !property.blind_bidding_started_at) {
    const blindDeadline = new Date(property.blind_bidding_deadline);
    if (now < blindDeadline) {
      return 'BLIND_WAITING';
    }
  }

  // BLIND_ACTIVE: Fase activa (con ofertas)
  if (property.blind_bidding_started_at && !property.blind_bidding_closed_at) {
    if (blindBiddingEnd && now < blindBiddingEnd) {
      return 'BLIND_ACTIVE';
    }
    // Contador terminó automáticamente → misma lógica que cierre manual
    if (blindBiddingEnd && now >= blindBiddingEnd) {
      if (startDate && startDate > blindBiddingEnd) {
        if (now >= startDate && now < deadline) {
          return 'OPEN';
        }
        return 'CLOSED';
      }
      return 'CLOSED';
    }
  }

  // ==========================================
  // PRIORIDAD 3: SUBASTA ABIERTA (POST-BLIND)
  // ==========================================

  // Sin fechas configuradas → UPCOMING (nueva propiedad sin configurar)
  if (!startDate && !deadline) {
    return 'UPCOMING';
  }

  // Solo deadline (LEGACY) → usar deadline para determinar estado
  if (!startDate && deadline) {
    return now < deadline ? 'OPEN' : 'CLOSED';
  }

  // Antes de inicio → UPCOMING
  if (now < startDate) {
    return 'UPCOMING';
  }

  // Durante periodo de pujas → OPEN
  if (deadline && now >= startDate && now < deadline) {
    return 'OPEN';
  }

  // Después del deadline → CLOSED
  return 'CLOSED';
};

/**
 * ✅ Verifica si se puede editar biddingStartDate
 * @param {object} property - Objeto de propiedad
 * @returns {boolean}
 */
export const canEditBiddingStartDate = (property) => {
  if (!property || !property.biddingStartDate) return true;
  
  const now = new Date();
  const startDate = new Date(property.biddingStartDate);
  
  // Solo se puede editar si aún no hemos llegado a la fecha de inicio
  return now < startDate;
};

/**
 * ✅ Verifica si se puede editar deadline
 * @param {object} property - Objeto de propiedad
 * @returns {boolean}
 */
export const canEditDeadline = (property) => {
  if (!property) return true;
  
  const now = new Date();
  const endDate = new Date(property.deadline);
  
  // Solo se puede editar si no hemos superado el deadline
  return now < endDate;
};

/**
 * ✅ Valida las fechas al crear/editar propiedad
 * @param {string} biddingStartDate - Fecha de inicio
 * @param {string} deadline - Fecha de cierre
 * @param {boolean} isEditing - Si está editando
 * @param {object} property - Propiedad original (solo en edición)
 * @returns {object} - { valid: boolean, error: string }
 */
export const validatePropertyDates = (biddingStartDate, deadline, isEditing = false, property = null) => {
  const now = new Date();
  const startDate = new Date(biddingStartDate);
  const endDate = new Date(deadline);
  
  // Validar que ambas fechas sean futuras
  if (startDate < now) {
    return {
      valid: false,
      error: 'La fecha de inicio de pujas debe ser futura.'
    };
  }
  
  if (endDate < now) {
    return {
      valid: false,
      error: 'La fecha de cierre debe ser futura.'
    };
  }
  
  // Validar que haya al menos 24h entre inicio y cierre
  const diffHours = (endDate - startDate) / (1000 * 60 * 60);
  if (diffHours < 24) {
    return {
      valid: false,
      error: 'Debe haber al menos 24 horas entre el inicio y el cierre de pujas.'
    };
  }
  
  return { valid: true, error: '' };
};